/**
 * AI Translate (แปลให้คนไข้) — the parts shared by POST /api/charts/:id/translate and
 * scripts/ai-translate-compare.mjs, so the script always tests the prompt that ships.
 * Spec + decisions: docs/ai-translate-spec.md.
 *
 * Plain JS with no Workers-only APIs (crypto.subtle exists in Node 18+ too).
 */

export const MODEL = 'claude-haiku-5-5'
// Decided 2026-10-25: `low` saved ~฿0.01 a chart and got tooth positions wrong.
export const EFFORT = 'medium'

/*
 * The glossary is the fix for the errors every model made in the two model tests
 * (docs/ai-translate-spec.md § Model test 1 / 2). Add a line here when a doctor's
 * shorthand comes out wrong; then re-run scripts/ai-translate-compare.mjs.
 */
const GLOSSARY = `คำย่อและศัพท์ของคลินิก (ใช้ตามนี้เสมอ):
- เลขฟันระบบ FDI: หลักแรก 1 = บนขวา, 2 = บนซ้าย, 3 = ล่างซ้าย, 4 = ล่างขวา (ขวา/ซ้ายของคนไข้)
  หลักที่สอง 1–2 = ฟันหน้า (ฟันตัด), 3 = ฟันเขี้ยว, 4–5 = ฟันกรามน้อย, 6–7 = ฟันกราม, 8 = ฟันกรามซี่สุดท้าย (ฟันซี่ในสุด)
  เช่น 16 = ฟันกรามบนขวาซี่แรก, 35 = ฟันกรามน้อยล่างซ้ายซี่ที่สอง, 12 = ฟันหน้าบนขวาซี่ที่สอง
- ด้านของฟัน: M = ด้านชิดฟันหน้า, D = ด้านชิดฟันหลัง, O = ด้านบดเคี้ยว, B = ด้านแก้ม, L = ด้านลิ้น, Pa (ต่อท้ายเลขฟัน/ด้านฟัน) = ด้านเพดาน
  เช่น "16OM" = อุดฟันซี่ 16 ด้านบดเคี้ยวและด้านชิดฟันหน้า
  Pa ที่ตามหลังซี่ฟันในประโยคเดียวกันหมายถึงด้านเพดานของซี่นั้น เช่น "12M leaked composite and Pa leaked temporary" = ซี่ 12 ด้านชิดฟันหน้าวัสดุอุดสีฟันรั่ว และด้านเพดานวัสดุอุดชั่วคราวรั่ว
- Pa ที่เป็นรายการเอกซเรย์หรือราคา = เอกซเรย์รายซี่ (ฟิล์มรอบปลายราก)
- BW = เอกซเรย์ซอกฟัน (ห้ามใช้คำว่า "ฟิล์มกัดปีก" หรือ "ภาพเอกซเรย์ปีก")
- radiolucent lesion ที่ถึงชั้น dentin = ฟันผุ — พูดตรง ๆ ว่า "ฟันผุ" ไม่ใช้คำว่า "รอยโปร่งแสง"
- PDL space กว้างขึ้น = มีการอักเสบรอบรากฟันเล็กน้อยที่เห็นจากเอกซเรย์ (ไม่ใช้คำว่า "เอ็นยึดปริทันต์")
- RCT = รักษารากฟัน, composite = วัสดุอุดสีเหมือนฟัน, temporary filling = วัสดุอุดชั่วคราว, leaked / marginal leakage = ขอบวัสดุอุดรั่ว
- OHI = แนะนำวิธีดูแลความสะอาดช่องปาก, scaling FM = ขูดหินปูนทั้งปาก, SC (ในรายการราคา) = ขูดหินปูน, polishing = ขัดฟัน
- Zoom = ฟอกสีฟันในคลินิก; gingival isolation = ทาเจลกั้นเหงือกเพื่อป้องกันน้ำยา (ไม่ใช่ยาง); light cure ในการฟอกสี = ฉายแสงกระตุ้นน้ำยาฟอกสี (แสงไม่ได้ทำให้เจลแข็ง)
- สีฟันที่ถ่ายก่อน/หลัง (เช่น pre op A3, post op A1) ต้องบอกเสมอว่า "สีฟันจาก A3 เป็น A1"
- nonfunctional tooth = ฟันที่ไม่ได้ใช้บดเคี้ยว (ห้ามเรียกว่า "ฟันคุด" ถ้าบันทึกไม่ได้เขียนว่า impacted)
- Refer = ส่งต่อ; Prosth = ทันตแพทย์ด้านครอบฟันและฟันเทียม — ถ้าบันทึกเขียนว่า crown ให้เรียกว่า "ครอบฟัน" (ห้ามเรียกว่าฟันปลอม); Sx = ศัลยกรรมช่องปาก (ถอนฟัน/ผ่าฟัน); Endo = รักษารากฟัน
- ชื่อหมอ: เขียนเป็นภาษาไทย เช่น dr.Golf = ทพ.กอล์ฟ`

export const SYSTEM = `คุณเป็นผู้ช่วยทันตแพทย์ที่คลินิกทันตกรรม Smile Seasons
หน้าที่: อ่านบันทึกการรักษา (dental chart) ที่ทันตแพทย์เขียนด้วยศัพท์ทางการแพทย์/ทันตกรรม แล้วแปลเป็นภาษาไทยที่เข้าใจง่ายและถูกต้อง
ผู้อ่านหลักคือเจ้าหน้าที่หน้าเคาน์เตอร์ซึ่งไม่ใช่บุคลากรทางการแพทย์ — ใช้เพื่อเข้าใจว่าหมอตรวจพบอะไร ทำอะไรวันนี้ แผนต่อไปคืออะไร
ค่ารักษาตามที่หมอเขียน และนัดครั้งต่อไป เพื่อทำนัด เก็บเงิน และตอบคำถามคนไข้ได้ถูกต้อง
ใช้ภาษาสุภาพ เพราะเจ้าหน้าที่อาจอ่านให้คนไข้ฟัง
ความถูกต้องสำคัญที่สุด: เลขซี่ฟัน ด้านของฟัน สิ่งที่ "ทำแล้ววันนี้" กับสิ่งที่ "วางแผนไว้" ต้องไม่ปนกัน

${GLOSSARY}

กติกา:
- ใช้ภาษาไทยที่สุภาพ เข้าใจง่าย หลีกเลี่ยงศัพท์เทคนิค ถ้าจำเป็นให้อธิบายในวงเล็บ
- เรียกฟันด้วยตำแหน่งตามตาราง FDI ข้างบน แล้วตามด้วยเลขซี่ เช่น "ฟันกรามบนขวาซี่แรก (ซี่ 16)" — ตรวจตำแหน่งบน/ล่าง ซ้าย/ขวา ทุกครั้ง
- อธิบายสั้น ๆ ว่าทำไมต้องรักษาแต่ละอย่าง
- ห้ามเพิ่มข้อมูลที่ไม่มีในบันทึก ห้ามวินิจฉัยเพิ่ม ห้ามคาดเดาสาเหตุ
- ค่ารักษา: แปลชื่อแต่ละรายการให้เข้าใจ (เช่น "Pa 200.-" = "เอกซเรย์รายซี่ 200 บาท") ตัวเลขต้องตรงตามที่หมอเขียนทุกตัว
  ห้ามรวมยอดเอง ห้ามคำนวณ ห้ามเดาราคาที่ไม่ได้เขียน — ถ้าหมอเขียนยอดรวมไว้ให้คงไว้ตามนั้น

ตอบเป็น JSON สองช่อง:
- patient_summary: markdown คำแปลของบันทึก ใช้หัวข้อ "## " ตามลำดับนี้ (ข้ามหัวข้อที่ไม่มีข้อมูล):
  ## สภาพช่องปากโดยรวม / ## ปัญหาที่พบ / ## สิ่งที่ทำวันนี้ / ## แผนการรักษาต่อไป / ## ค่ารักษา / ## นัดครั้งต่อไป
  ใช้ bullet สั้น ๆ ทุกข้อที่เกี่ยวกับฟันต้องระบุซี่ฟัน ข้อสงสัยให้ใส่ใน staff_notes ไม่ใช่ช่องนี้
- staff_notes: ไม่เกิน 3 ข้อ เฉพาะเรื่องที่ต้องถามทันตแพทย์ก่อนนัด เก็บเงิน หรืออธิบายคนไข้
  (เช่น "ไม่มีอาการสำคัญ (CC) ในบันทึก" หรือคำย่อที่อ่านได้สองความหมาย) ห้ามพูดถึงกติกาหรือคำศัพท์ในคำสั่งนี้
  แต่ละข้อขึ้นบรรทัดใหม่ขึ้นต้นด้วย "- " ถ้าไม่มีให้เป็นข้อความว่าง`

// patient_summary first: the browser streams it into view while it is still being
// written, and staff_notes only matters once the whole answer is in.
export const SCHEMA = {
  type: 'object',
  properties: {
    patient_summary: { type: 'string' },
    staff_notes: { type: 'string' },
  },
  required: ['patient_summary', 'staff_notes'],
  additionalProperties: false,
}

export function requestBody(clinicalText, { model = MODEL, effort = EFFORT, stream = false } = {}) {
  return {
    model,
    max_tokens: 16000,
    stream,
    thinking: { type: 'adaptive' },
    output_config: { effort, format: { type: 'json_schema', schema: SCHEMA } },
    system: SYSTEM,
    messages: [{ role: 'user', content: clinicalText }],
  }
}

// The chart editor stores PE as HTML (and older rows as Bubble BBCode).
export function htmlToText(s) {
  return String(s ?? '')
    .replace(/\[\/?[a-zA-Z][^\]]*\]/g, (t) => (/^\[(li|\/li|br)\b/i.test(t) ? '\n' : ''))
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li[^>]*>/gi, '\n- ')
    .replace(/<\/(p|div|li|ul|ol|h\d)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .split('\n').map((l) => l.replace(/[ \t]+/g, ' ').trim()).filter(Boolean).join('\n')
}

/*
 * The text sent to Claude, and the text the regenerate gate hashes: everything the
 * doctor wrote, including the price line. No name, HN, phone or ID card ever reaches
 * it — the chart row carries none, and nothing here reads the patient record.
 */
export function buildClinicalText(chart) {
  const out = []
  const hx = Array.isArray(chart?.hx) ? chart.hx : []
  const cc = chartField(chart, 'cc')
  out.push(`CHIEF COMPLAINT: ${cc || '(blank)'}`)
  hx.forEach((e, i) => {
    const pe = htmlToText(e?.PE)
    const note = String(e?.note ?? '').trim()
    if (!pe && !note) return
    const teeth = Array.isArray(e?.tag) ? e.tag.filter((t) => t !== e?.tag_type) : []
    const head = [`#${i + 1}`, e?.tag_type || 'Full Mouth', teeth.length ? `teeth ${teeth.join(', ')}` : '']
      .filter(Boolean).join(' ')
    out.push('', `ORAL EXAM & TREATMENT — ${head}`)
    if (pe) out.push(pe)
    if (note) out.push(`Note: ${note}`)
  })
  const oral = (Array.isArray(chart?.oral_exam) ? chart.oral_exam : [])
    .map((o) => [o?.tag, o?.type, htmlToText(o?.detail)].map((v) => String(v ?? '').trim()).filter(Boolean).join(' · '))
    .filter(Boolean)
  if (oral.length) out.push('', 'ORAL EXAM FINDINGS:', ...oral.map((l) => `- ${l}`))
  const price = chartField(chart, 'price')
  if (price) out.push('', `TREATMENT PRICE: ${price}`)
  const nv = chartField(chart, 'NV')
  if (nv) out.push('', `NEXT VISIT: ${nv}`)
  return out.join('\n')
}

// Same rule as ClinicalEditor's makeDraft: chart-level cc / price / NV live on
// `chart.history`; older rows stored them on the first non-empty hx[] entry.
function chartField(chart, k) {
  const v = String(chart?.history?.[k] ?? '').trim()
  if (v) return v
  for (const e of Array.isArray(chart?.hx) ? chart.hx : []) {
    const x = String(e?.[k] ?? '').trim()
    if (x) return x
  }
  return ''
}

// True when there is nothing a doctor wrote to translate.
export function isClinicalEmpty(chart) {
  const hx = Array.isArray(chart?.hx) ? chart.hx : []
  return !hx.some((e) => htmlToText(e?.PE) || String(e?.note ?? '').trim())
}

// A chart that was never translated carries the JSON column's empty default ("" / {}),
// not null — only an object with text counts as a saved translation.
export function validSummary(s) {
  return !!(s && typeof s === 'object' && typeof s.patient_summary === 'string' && s.patient_summary.trim())
}

export async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

// USD per million tokens [input, output] — for the cost log and the compare script.
export const PRICE = { 'claude-haiku-5-5': [0.10, 0.50], 'claude-sonnet-5-5': [2, 10], 'claude-opus-5-5': [4, 20] }
